(() => {
  if (window.__ckLanguageSwitcherInit) return;
  window.__ckLanguageSwitcherInit = true;

  // Ensure utils is loaded
  if (typeof window.requireUtils === 'function') {
    window.requireUtils('language-switcher.js');
  }

  if (!window.utils) {
    console.error('[language-switcher] Utils not loaded!');
    return;
  }

  const ROOT = '[data-language-switcher]';
  const ITEM = '[data-language-switcher-item]';
  const TRIGGER = '[data-language-switcher-trigger]';

  const norm = (v) => (window.utils?.normalizeStringValue(v) || '').trim().toLowerCase();
  const parseShopifyUrl =
    window.utils?.parseShopifyUrl || (() => ({ market: null, lang: 'en', remainder: [] }));
  const buildShopifyUrl = window.utils?.buildShopifyUrl || (() => '/');

  /**
   * Get market handle from DOM data attribute (set by Liquid)
   * Maps Shopify market handle to URL code (e.g., 'european-union' -> 'eu')
   * @returns {string|null} Market URL code (e.g., 'eu', 'us', 'ca') or null
   */
  const getMarketHandle = () => {
    const root = document.querySelector(ROOT);
    if (root) {
      const handle = root.getAttribute('data-market-handle');
      if (handle) {
        // Map Shopify market handle to URL code
        const urlCode = window.utils?.mapMarketHandleToUrlCode(handle);
        return urlCode ? norm(urlCode) : norm(handle);
      }
    }
    return null;
  };

  /**
   * Check if language switcher is present in DOM (safety check)
   * @returns {boolean}
   */
  const isSwitcherPresent = () => {
    return document.querySelector(ROOT) !== null;
  };

  /**
   * Update trigger text to show current language code
   * @param {string} code - Language ISO code
   */
  const setTrigger = (code) => {
    if (!isSwitcherPresent()) return;

    document.querySelectorAll(ROOT).forEach((root) => {
      const el = root.querySelector(TRIGGER);
      if (el) el.textContent = String(code || 'en').toUpperCase();
    });
  };

  /**
   * Handle language item click
   */
  document.addEventListener('click', (e) => {
    const item = e.target?.closest?.(ITEM);
    if (!item) return;

    if (!isSwitcherPresent()) return;

    const root = item.closest?.(ROOT);
    if (!root) return;

    root.dispatchEvent(new CustomEvent('ck-popover-close', { bubbles: true, composed: true }));

    const targetLang = item.getAttribute('data-language-switcher-iso') || 'en';
    const marketHandle = getMarketHandle();

    if (!marketHandle) {
      console.warn('[language-switcher] Market handle not available');
      return;
    }

    const parsed = parseShopifyUrl(window.location.pathname);
    const marketForUrl = marketHandle === 'uk' ? null : marketHandle;

    const newUrl = buildShopifyUrl({
      lang: targetLang,
      market: marketForUrl,
      remainder: parsed.remainder || [],
      pathname: window.location.pathname,
    });

    setTrigger(targetLang);

    // Store language in sessionStorage (with error handling for private browsing)
    if (typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.setItem('__ck_target_lang', targetLang);
      } catch (e) {
        console.warn('[language-switcher] Error storing target language:', e);
      }
    }

    // Store user's explicit language preference in localStorage (persists across page loads)
    // Wrapped in try-catch to handle private browsing mode or disabled storage
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('__ck_user_lang_preference', targetLang);
      } catch (e) {
        console.warn('[language-switcher] Error storing language preference:', e);
        // Continue execution even if storage fails - redirect should still work
      }
    }

    if (window.Weglot && typeof window.Weglot.switchTo === 'function') {
      try {
        window.Weglot.switchTo(targetLang);
      } catch (e) {
        console.warn('[language-switcher] Error triggering Weglot:', e);
      }
    }

    window.location.assign(newUrl);
  });

  /**
   * Sync trigger text with current language from URL and trigger Weglot if needed
   */
  const sync = () => {
    const parsed = parseShopifyUrl(window.location.pathname);
    let storedLang = null;
    if (typeof sessionStorage !== 'undefined') {
      try {
        storedLang = sessionStorage.getItem('__ck_target_lang');
      } catch (e) {
        console.warn('[language-switcher] Error reading stored language:', e);
      }
    }
    const targetLang = storedLang || parsed.lang || 'en';

    if (storedLang && typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.removeItem('__ck_target_lang');
      } catch (e) {
        console.warn('[language-switcher] Error removing stored language:', e);
      }
    }

    if (window.Weglot && typeof window.Weglot.switchTo === 'function') {
      const currentWeglotLang = window.Weglot.getCurrentLang && window.Weglot.getCurrentLang();
      if (currentWeglotLang !== targetLang) {
        try {
          window.Weglot.switchTo(targetLang);
        } catch (e) {
          console.warn('[language-switcher] Error triggering Weglot:', e);
        }
      }
    }

    if (!isSwitcherPresent()) return;

    const marketHandle = getMarketHandle();
    const isUKMarket = !parsed.market && marketHandle === 'uk';
    const isMatchingMarket = parsed.market === marketHandle;

    if (isUKMarket || isMatchingMarket) {
      setTrigger(parsed.lang);
    }
  };

  /**
   * Check if country is Spanish-speaking and redirect to Spanish if needed
   * Only runs on initial page load, not when user explicitly switches languages
   * Only redirects if country is actually in the EU market (not UK or other markets)
   */
  const checkSpanishCountryRedirect = () => {
    // Don't redirect if user explicitly chose a non-Spanish language (check localStorage)
    // This preference persists across page navigations
    let userLangPreference = null;
    if (typeof localStorage !== 'undefined') {
      try {
        userLangPreference = localStorage.getItem('__ck_user_lang_preference');
      } catch (e) {
        console.warn('[language-switcher] Error reading language preference:', e);
      }
    }
    if (userLangPreference && userLangPreference !== 'es') {
      // User has explicitly chosen a non-Spanish language, respect their choice
      return;
    }

    // Get market handle from meta tag (set by Liquid)
    const marketMeta = document.querySelector('meta[name="market-handle"]');
    const marketHandle = marketMeta ? marketMeta.getAttribute('content') : null;

    // Only redirect if market is EU (not UK or other markets)
    // If country is not in EU market, Shopify serves UK/default market - don't redirect
    if (!marketHandle || marketHandle === 'uk') return;

    // Spanish-speaking country codes in EU market (only Spain is in the EU market)
    const spanishCountries = ['ES'];

    // Get country from meta tag (set by Liquid)
    const countryMeta = document.querySelector('meta[name="country-code"]');
    const countryCode = countryMeta ? countryMeta.getAttribute('content') : null;

    // Debug logging for staging/preview mode
    console.log('[language-switcher] Spanish redirect check:', {
      countryCode,
      marketHandle,
      isSpanishCountry: countryCode && spanishCountries.includes(countryCode),
      pathname: window.location.pathname,
    });

    // Only redirect if country is Spanish-speaking AND in EU market
    if (!countryCode || !spanishCountries.includes(countryCode)) return;

    // Get current language from URL
    const parsed = parseShopifyUrl(window.location.pathname);
    if (parsed.lang !== 'en') return;

    // Only redirect if on homepage or en-eu routes
    const pathname = window.location.pathname;
    const search = window.location.search;
    const hash = window.location.hash;

    // Check if this pathname should trigger a redirect
    const shouldRedirect =
      pathname === '/' || pathname === '/en-eu' || pathname.startsWith('/en-eu/');

    if (!shouldRedirect) return;

    // Don't redirect if we've already checked for this specific pathname (prevent loops)
    // Use pathname-specific flag to allow redirects on different pages
    const redirectKey = `__ck_spanish_redirect_${pathname}`;
    let redirectChecked = null;
    if (typeof sessionStorage !== 'undefined') {
      try {
        redirectChecked = sessionStorage.getItem(redirectKey);
      } catch (e) {
        console.warn('[language-switcher] Error reading redirect flag:', e);
      }
    }
    if (redirectChecked) return;

    // Set flag only when we're about to redirect (prevents loops for this specific pathname)
    if (typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.setItem(redirectKey, 'true');
      } catch (e) {
        console.warn('[language-switcher] Error setting redirect flag:', e);
        // Continue execution even if storage fails - redirect should still work
      }
    }

    console.log('[language-switcher] Redirecting to Spanish:', pathname, '-> /es-eu');

    // Perform the redirect
    if (pathname === '/') {
      window.location.replace('/es-eu' + search + hash);
    } else if (pathname === '/en-eu') {
      window.location.replace('/es-eu' + search + hash);
    } else if (pathname.startsWith('/en-eu/')) {
      const newPath = pathname.replace('/en-eu/', '/es-eu/');
      window.location.replace(newPath + search + hash);
    }
  };

  const init = () => {
    // Check for Spanish country redirect first (only on initial load)
    checkSpanishCountryRedirect();

    const trySync = (attempts = 0) => {
      sync();

      const parsed = parseShopifyUrl(window.location.pathname);
      if (parsed.lang && window.Weglot && typeof window.Weglot.switchTo === 'function') {
        const currentWeglotLang = window.Weglot.getCurrentLang && window.Weglot.getCurrentLang();
        if (currentWeglotLang !== parsed.lang) {
          window.Weglot.switchTo(parsed.lang);
        }
      } else if (!parsed.market && window.Weglot && typeof window.Weglot.switchTo === 'function') {
        const currentWeglotLang = window.Weglot.getCurrentLang && window.Weglot.getCurrentLang();
        if (currentWeglotLang !== 'en') {
          window.Weglot.switchTo('en');
        }
      }

      if (!window.Weglot && attempts < 10) {
        setTimeout(() => trySync(attempts + 1), 100);
      }
    };

    trySync();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('popstate', sync);
})();
